import React, { useContext, useMemo } from 'react'

import { Loader, makeAppStyles } from '@percept/mui'

import { ArrowDownward, ArrowUpward } from '@percept/mui/icons'

import { DashboardCard } from 'components/DashboardCard'

import { OrgUnitContext, ReportProviderContext, TimeseriesContext } from '@percept/app-components'

import {
  ProviderCategoryScoreTimeseriesDatum,
  useOrgUnitCategoryScores,
} from '@percept/hooks'

import { avg, getComparisonFunction } from '@percept/utils'

import { ReportProviderV2 } from '@percept/types'

import { roundedRateFormatter } from '@percept/mui/charts'

import { getPreviousTimeseriesDateRange } from './lib'


const useStyles = makeAppStyles( theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    fontSize: 40,
    fontWeight: theme.typography.fontWeightBold,
  },
  percentageValue: {
    display: 'flex',
    alignItems: 'center',
    height: 60,
  },
  notApplicable: {
    color: theme.palette.text.disabled,
  },
  positive: {
    marginLeft: theme.spacing(1.5),
    color: theme.chart.healthColourScale(1),
    fontSize: 42,
    position: 'relative',
    top: -2,
  },
  negative: {
    marginLeft: theme.spacing(1.5),
    color: theme.chart.healthColourScale(0),
    fontSize: 42,
    position: 'relative',
    top: -2,
  },
  errorContent: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
    fontSize: 16,
    color: 'text.secondary',
    fontWeight: theme.typography.fontWeightMedium,
  },
}))


const comparisonFunction = getComparisonFunction('ABSOLUTE')


const deriveOverallScore = (
  data: ProviderCategoryScoreTimeseriesDatum[] | undefined,
  provider: ReportProviderV2 | null,
): number | null => {
  if( !data || !data.length ){
    return null
  }
  const matchingProviders = provider ? data.filter( d => d.provider === provider ) : data
  const latestHealths: number[] = []
  for( const datum of matchingProviders ){
    const overall = datum.timeseriesByCategory.OVERALL
    if( !overall || !overall.length ){
      continue
    }
    const latest = overall[overall.length - 1].health
    if( latest !== null ){
      latestHealths.push(latest)
    }
  }
  if( !latestHealths.length ){
    return null
  }
  return avg(latestHealths)
}


export const OverallSetupScoreSummary = () => {
  const orgUnit = useContext(OrgUnitContext)

  const provider = useContext(ReportProviderContext)

  const { start, end, granularity } = useContext(TimeseriesContext)

  const categoryScoresQuery = useOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    start,
    end,
    granularity,
    enabled: !!start,
    retry: false,
  })

  const previousCategoryScoresQuery = useOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...getPreviousTimeseriesDateRange(start, granularity),
    granularity,
    enabled: !!start,
    retry: false,
  })

  const overallScore = useMemo(
    () => deriveOverallScore(categoryScoresQuery.data, provider),
    [categoryScoresQuery.data, provider]
  )

  const previousOverallScore = useMemo(
    () => deriveOverallScore(previousCategoryScoresQuery.data, provider),
    [previousCategoryScoresQuery.data, provider]
  )

  const change = (
    overallScore !== null && previousOverallScore !== null ?
      comparisonFunction(overallScore, previousOverallScore) :
      null
  )

  const classes = useStyles()

  return (
    <DashboardCard
      title='Overall Setup Score'
      tooltipContent={
        provider ?
          'The provider-level setup score for the selected day. Higher is better.' :
          'The average setup score across all platforms for the selected day. Higher is better.'
      }
      isRefetching={categoryScoresQuery.isRefetching}>
      { !categoryScoresQuery.data && categoryScoresQuery.error ? (
        <div className={classes.errorContent}>
          Data could not be loaded
        </div>
      ) : !categoryScoresQuery.data ? (
        <Loader preset='centered' minHeight='10rem' />
      ) : overallScore === null ? (
        <div className={classes.content}>
          <div className={classes.percentageValue}>
            <div className={classes.notApplicable}>N / A</div>
          </div>
        </div>
      ) : (
        <div className={classes.content}>
          <div className={classes.percentageValue}>
            { roundedRateFormatter(overallScore) }
            { !!change && (
              Number(change) > 0 ? (
                <ArrowUpward className={classes.positive} fontSize='large' />
              ) : (
                <ArrowDownward className={classes.negative} fontSize='large' />
              )
            )}
          </div>
        </div>
      )}
    </DashboardCard>
  )
}
